import { ProjectModel } from 'api-hooks/projects';
import stylesColors from 'common/styles/styles.colors';
import { generateId } from 'common/utils/string';
import Separator from 'components/common/separator';
import { useFormState } from 'components/elements/form';
import TableInput from 'components/elements/table/table-input';
import { ActionType, ColumnDataType } from 'components/elements/table/type';
import Text from 'components/elements/text';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { ProjectItineraryFormType, ProjectFormType } from './form-type';

interface ItinenaryTableProps {
  project?: ProjectModel;
}

const name = 'projectItineraries' as const;

function useGetItineraryColumns(project?: ProjectModel) {
  const { t } = useTranslation();
  const { editable } = useFormState();
  return [
    {
      header: t('common:date'),
      accessorKey: 'date',
      minSize: 300,
      maxSize: 300,
      dataType: ColumnDataType.date,
    },
    {
      header: t('common:route_detail'),
      accessorKey: 'route',
      minSize: 300,
      maxSize: 300,
      dataType: ColumnDataType.text,
    },
    {
      header: t('common:description'),
      accessorKey: 'description',
      minSize: 300,
      maxSize: 300,
      dataType: ColumnDataType.text,
    },
    ...(project
      ? [
          {
            header: t('common:is_complete'),
            accessorKey: 'isComplete',
            minSize: 120,
            maxSize: 120,
            dataType: ColumnDataType.checkbox,
          },
        ]
      : []),
    ...(editable
      ? [
          {
            header: '',
            accessorKey: 'delete',
            minSize: 40,
            maxSize: 40,
            dataType: ColumnDataType.delete,
          },
        ]
      : []),
  ];
}

export default function ItineraryTable(props: ItinenaryTableProps) {
  const { editable } = useFormState();
  const { project } = props;

  const { formState, control, clearErrors, getValues, trigger } =
    useFormContext<ProjectFormType>();
  const { errors } = formState;

  const { fields, update, append, remove } = useFieldArray({
    control,
    name,
    keyName: '__customId',
  });

  const errorMessage = errors?.[name]?.message;
  const columns = useGetItineraryColumns(project);
  const dispatch = React.useCallback(
    (action) => {
      const itinenaries = getValues(name);
      const currentRowIndex = action.rowIndex as number;
      const itinenary = itinenaries[currentRowIndex];
      const type = action.type as ActionType;
      const columnId = action.columnId as keyof ProjectItineraryFormType;
      const value = action.value;

      if (type === 'addRow') {
        append({
          date: null,
          description: '',
          id: undefined,
          isComplete: false,
          route: null,
          customId: generateId(),
        });
        trigger(name);
      } else if (type === 'removeRow') {
        remove(currentRowIndex);
        trigger(name);
      } else if (type === 'update_cell') {
        update(currentRowIndex, {
          ...itinenary,
          [columnId]: value,
        });
        clearErrors(`${name}.${currentRowIndex}.${columnId}`);
      }
    },
    [append, clearErrors, getValues, remove, trigger, update],
  );

  return (
    <>
      <div style={{ width: '100%' }}>
        <TableInput
          columns={columns}
          data={fields}
          dispatch={dispatch}
          disabled={!editable}
          name={name}
          uniqueRowKey="customId"
        />
      </div>
      {errorMessage && (
        <>
          <Separator gap={16} />
          <Text variant="LabelSmall" color={stylesColors.redDark}>
            {errorMessage}
          </Text>
        </>
      )}
    </>
  );
}
